import {
    put,
    select,
    takeLatest
} from "redux-saga/effects";
import { Routes } from "../../constants/routes";
import {
    PatientIndividualInformationDto,
    ErrorCodes,
    TypeOfFinalTransaction,
    InteractionType
} from "../../dtos/KioskDtos.dtos";
import TimerManager from "../../services/TimerManager";
import { resetAllStores } from "../../store";
import {
    selectHealthCardAcquiringMode,
    toggleShowPostalCodeConfirmationModal,
    changeHealthCardNumber
} from "../../store/AcquireHealthCardNumber/actions";
import { ACQUIRING_MODE } from "../../store/AcquireHealthCardNumber/type";
import {
    getOptionRegionCodeConfirmationRequiredForSaga
} from "../../store/AdminOptions/selectors";
import { showErrorUpdateMessage } from "../../store/Errors/actions";
import {
    changeLanguage,
    toggleLanguageSelectionVisibility
} from "../../store/LanguageLocale/actions";
import {
    selectPatientCreationStatus,
    stagePatientInformation
} from "../../store/PatientInformationForm/actions";
import { PATIENT_INFO_MODE } from "../../store/PatientInformationForm/type";
import {
    FINISHED_CONFIRMING_POSTAL_CODE,
    RETURN_TO_INTRODUCTION_SCREEN,
    START_APPLICATION_AFTER_SETTING_UP_ADMIN_OPTIONS,
    CANCEL_POSTAL_CODE_CONFIRMATION,
    SUBMIT_HEALTH_CARD_NUMBER_TO_SERVER
} from "./constant";
import {
    SubmitHealthCardNumberToServerAction,
    StartApplicationAfterSettingAdminOptionsAction
} from "./types";
import Client from "../../services/Client";
import { LanguageLocaleService } from "../../services/Language/LanguageService";
import { getTextLibraryForSaga } from "../../store/LanguageLocale/selector";
import { LanguageCodes } from "../../constants/languages";
import { toggleNetworkLoadingSpinnerVisibility } from "../../store/LoadingSpinner/actions";
import { redirectToSurvey } from "../surveyRedirectSaga";
import { push } from "../../store/Routing/actions";
import { getPatientInfoFromServer } from "../PatientInfo/actions";
import { debugDispatchSaga } from "../debugDispatchSaga";

export function* finishedGettingPatientDataFromServerSaga(patientIndividualInformation: PatientIndividualInformationDto) {
    yield TimerManager.stopInactivityListener();
    if (yield select(getOptionRegionCodeConfirmationRequiredForSaga)) {
        yield put(toggleNetworkLoadingSpinnerVisibility(false))
        if (patientIndividualInformation.addresses.length === 0) {
            Client.logInteractionEvent(InteractionType.UnableValidatePostalCodeNoAddressesFoundForPatient);
            yield put(redirectToSurvey(showErrorUpdateMessage(true, ErrorCodes.NoAddressesFound, true), TypeOfFinalTransaction.EncounteredError, ErrorCodes.NoAddressesFound));
        } else {
            yield put(stagePatientInformation(patientIndividualInformation));
            yield put(toggleShowPostalCodeConfirmationModal(true));
            yield put(selectPatientCreationStatus(PATIENT_INFO_MODE.UPDATING));
        }
    } else {
        yield debugDispatchSaga("Patient Information Loaded", false, patientIndividualInformation);
        yield put(stagePatientInformation(patientIndividualInformation));
        yield put(selectPatientCreationStatus(PATIENT_INFO_MODE.UPDATING));
        yield put(toggleNetworkLoadingSpinnerVisibility(false));
        yield put(push(Routes.patientOverview));
    }
    yield TimerManager.resetInactivityListener();
}

export function* cancelPostalCodeConfirmationSaga() {
    yield put(resetAllStores());
    yield put(selectHealthCardAcquiringMode(ACQUIRING_MODE.SCANNER));
    yield put(toggleShowPostalCodeConfirmationModal(false));
}

export function* finishedConfirmingPostalCodeSaga() {
    yield put(push(Routes.patientOverview));
}

export function* submitHealthCardNumberToServerSaga(action: SubmitHealthCardNumberToServerAction) {
    const { healthCardNumber, acquisitionType } = action.payload;
    yield put(changeHealthCardNumber(healthCardNumber));
    yield put(getPatientInfoFromServer(healthCardNumber, acquisitionType));
}

export function* returnToIntroductionScreenSaga() {
    yield put(resetAllStores());
    yield put(selectHealthCardAcquiringMode(ACQUIRING_MODE.SCANNER));
    yield put(push(Routes.introduction));
}

export function* startApplicationSaga(action: StartApplicationAfterSettingAdminOptionsAction) {
    yield put(toggleNetworkLoadingSpinnerVisibility(true));
    try {
        yield Client.startApplication(action.payload);
        yield TimerManager.startTimer(action.payload.sessionTimeSpan);
        yield put(changeLanguage(action.payload.lang));
        yield put(toggleLanguageSelectionVisibility(true));
        yield put(push(Routes.introduction));
    } catch (e) {
        const textLibrary: LanguageLocaleService = yield select(getTextLibraryForSaga);
        const textString = textLibrary
            ? textLibrary
                .setAndGetCurrentLanguageResource(navigator.language.substring(0, 2) as LanguageCodes)
                .getGeneralStrings()
                .messagesInstructions
                .UnableToStartApplicationMessage
            : "Error. Erreur";
        // Show native simple alert and reload application.
        yield alert(textString);
        window.location.reload();
    }
    yield put(toggleNetworkLoadingSpinnerVisibility(false));
}

export function* watchApplicationAsyncSagas() {
    yield takeLatest(SUBMIT_HEALTH_CARD_NUMBER_TO_SERVER, submitHealthCardNumberToServerSaga)
    yield takeLatest(CANCEL_POSTAL_CODE_CONFIRMATION, cancelPostalCodeConfirmationSaga);
    yield takeLatest(FINISHED_CONFIRMING_POSTAL_CODE, finishedConfirmingPostalCodeSaga);
    yield takeLatest(RETURN_TO_INTRODUCTION_SCREEN, returnToIntroductionScreenSaga);
    yield takeLatest(START_APPLICATION_AFTER_SETTING_UP_ADMIN_OPTIONS, startApplicationSaga);
}


// WEBPACK FOOTER //
// ./src/sagas/ApplicationAsyncSagas/sagas.ts